import { z } from "zod";
import { isoDateTimeSchema } from "./profile.js";
import { serverNameSchema } from "./server.js";
import { wovenIdSchema } from "./server-connection.js";
const documentIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/)
    .refine((id) => !/^__.*__$/.test(id), { message: "Firestore-reserved ID" });
const levelSchema = z.enum(["info", "warn", "error"]);
function boundedUtf8Message(message) {
    let bytes = 0;
    for (const character of message) {
        const code = character.codePointAt(0);
        bytes += code <= 0x7f ? 1 : code <= 0x7ff ? 2 : code <= 0xffff ? 3 : 4;
        if (bytes > 1024)
            return false;
    }
    return true;
}
const messageSchema = z
    .string()
    .min(1)
    .max(1024)
    .refine(boundedUtf8Message, "message exceeds 1024 UTF-8 bytes");
const sourceSchema = z.enum(["host", "client", "node"]);
const eventSchema = z.enum([
    "server.registered",
    "client.log",
    "client.connected",
    "client.disconnected",
]);
function validSourceEvent({ source, event }) {
    return ((source === "host" && event === "server.registered") ||
        (source === "client" && event === "client.log") ||
        (source === "node" && (event === "client.connected" || event === "client.disconnected")));
}
export const logEntrySchema = z
    .object({
    id: documentIdSchema,
    occurredAt: isoDateTimeSchema,
    level: levelSchema,
    source: sourceSchema,
    event: eventSchema,
    resourceId: z.literal("global"),
    serverId: documentIdSchema,
    serverName: serverNameSchema,
    message: messageSchema,
    namespaceId: wovenIdSchema.optional(),
    sessionId: wovenIdSchema.optional(),
    connectionId: wovenIdSchema.optional(),
})
    .refine(validSourceEvent, "invalid log source/event combination");
export const wovenLogSequenceSchema = z
    .string()
    .refine((value) => /^(?:0|[1-9][0-9]{0,19})$/.test(value) && BigInt(value) <= 18446744073709551615n, "invalid u64 sequence");
export const wovenLogIncarnationSchema = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
export const wovenLogFeedEntrySchema = z
    .object({
    sequence: wovenIdSchema,
    occurredAtMs: z.number().int().nonnegative().max(253402300799999),
    namespaceId: wovenIdSchema,
    sessionId: wovenIdSchema,
    connectionId: wovenIdSchema,
    source: z.enum(["client", "node"]),
    event: z.enum(["client.log", "client.connected", "client.disconnected"]),
    level: levelSchema,
    message: messageSchema,
})
    .strict()
    .refine(validSourceEvent, "invalid log source/event combination");
export const wovenLogFeedSchema = z
    .object({
    nodeIncarnation: wovenLogIncarnationSchema,
    entries: z.array(wovenLogFeedEntrySchema).max(32),
    nextSequence: wovenLogSequenceSchema,
    droppedThrough: wovenLogSequenceSchema,
})
    .strict();
export const listLogsQuerySchema = z
    .object({
    resourceId: z.literal("global").optional(),
    serverId: documentIdSchema.optional(),
    level: levelSchema.optional(),
    cursor: documentIdSchema.optional(),
    limit: z
        .union([
        z.number(),
        z
            .string()
            .regex(/^[1-9][0-9]{0,2}$/)
            .transform(Number),
    ])
        .pipe(z.number().int().min(1).max(100))
        .default(50),
})
    .strict();
export const listLogsResponseSchema = z.object({
    items: z.array(logEntrySchema).max(100),
    nextCursor: documentIdSchema.nullable(),
});
//# sourceMappingURL=logs.js.map